<template>
    <div class="select-wrapper">
        <el-tooltip
            :content="selectedLabelText"
            placement="top"
            :disabled="tooltipDisabled"
        >
            <el-select
                ref="el_select"
                :clearable="clearable"
                :disabled="disabled"
                v-model="Content"
                :multiple="multiple"
                :collapse-tags="collapseTags"
                :placeholder="disabled ? '' : placeholder"
                @change="onChange"
                @input="onInput"
                :filterable="filterable"
                :no-data-text="'Không có bản ghi nào'"
                :no-match-text="'Không tìm thấy bản ghi nào'"
                :size="size"
                :class="{
                    'active-dislabel': disabled,
                    'error-select': textError.length > 0
                }"
                :popper-class="popperClass"
                @visible-change="onVisibleChange"
            >
                <li
                    style="border-bottom: 1px solid #e4e7ed"
                    v-if="multiple"
                    class="el-select-dropdown__item select-all-item"
                >
                    <label class="select-all-label">
                        <input
                            type="checkbox"
                            @change="onCheckAllChange"
                            v-model="CheckAll"
                            class="select-all-checkbox"
                        />
                        <span class="select-all-text">--Tất cả--</span>
                    </label>
                </li>
                <el-option
                    v-for="(item, index) in data"
                    :key="index"
                    :label="getLabel(item)"
                    :disabled="item.disabled"
                    :value="getValue(item)"
                >
                </el-option>
            </el-select>
        </el-tooltip>
        <span v-if="textError.length > 0" class="text-error-input">
            {{ textError }}
        </span>
    </div>
</template>

<script>
export default {
    props: {
        clearable: { default: true },
        value: { required: true },
        disabled: { type: Boolean, default: false },
        data: { type: Array, default: () => [], required: true },
        placeholder: { default: 'Chọn' },
        filterable: { type: Boolean, default: true },
        multiple: { type: Boolean, default: false },
        'collapse-tags': { type: Boolean, default: true },
        fields: { type: Array, required: true },
        size: { type: String, default: 'medium' },
        textError: { type: String, default: '' }
    },
    watch: {
        value: {
            handler(newValue) {
                this.Content = newValue || (this.multiple ? [] : null)
                this.updateCheckAllState()
            },
            immediate: true
        },
        data: {
            handler() {
                this.updateCheckAllState()
            },
            immediate: true
        }
    },
    data() {
        return {
            Content: this.value,
            CheckAll: false
        }
    },
    computed: {
        popperClass() {
            // Thêm class 'custom-tick-select' để dễ style CSS global
            let cls = 'e-select-dropdown-' + this._uid + ' custom-tick-select'
            if (this.CheckAll) {
                cls += ' mode-select-all'
            }
            return cls
        },
        selectedLabelText() {
            if (this.multiple && Array.isArray(this.Content)) {
                if (this.Content.length === 0) return ''
                const firstVal = this.Content[0]
                const item = this.data.find((d) => this.getValue(d) === firstVal)
                return item ? this.getLabel(item) : String(firstVal)
            }
            if (!this.multiple && this.Content != null && this.Content !== '') {
                const item = this.data.find((d) => this.getValue(d) === this.Content)
                return item ? this.getLabel(item) : String(this.Content)
            }
            return ''
        },
        tooltipDisabled() {
            if (!this.selectedLabelText) return true
            if (String(this.selectedLabelText).length <= 20) return true
            return false
        }
    },
    methods: {
        updateCheckAllState() {
            if (this.multiple && Array.isArray(this.Content)) {
                this.CheckAll =
                    this.data.length > 0 && this.Content.length === this.data.length
            }
        },
        onCheckAllChange() {
            if (this.CheckAll) {
                this.Content = []
                if (this.fields[1]) {
                    this.data.forEach((obj) => {
                        this.Content.push(obj[this.fields[1]])
                    })
                }
            } else {
                this.Content = []
            }
            this.$emit('input', this.Content)
            this.$emit('change', this.Content)
        },
        onChange(e) {
            this.updateCheckAllState()
            this.$emit('change', e)
        },
        onInput(e) {
            this.$emit('input', e)
        },
        getLabel(item) {
            return this.fields[0] ? item[this.fields[0]] : ''
        },
        getValue(item) {
            return this.fields[1] ? item[this.fields[1]] : ''
        },
        onVisibleChange(visible) {
            if (visible) {
                this.$nextTick(() => {
                    try {
                        // Chọn đúng dropdown dựa trên UID class
                        const popper = document.querySelector(
                            '.e-select-dropdown-' + this._uid
                        )
                        const trigger =
                            this.$refs.el_select && this.$refs.el_select.$el
                        if (popper && trigger) {
                            const rect = trigger.getBoundingClientRect()
                            // Ép dropdown luôn bằng đúng width của input (chuẩn UI/UX)
                            popper.style.minWidth = rect.width + 'px'
                            popper.style.width = rect.width + 'px'
                        }
                    } catch (err) {
                        // best-effort, ignore lỗi DOM
                    }
                })
            }
        }
    }
}
</script>

<style scoped>
/* CSS nội bộ component */
.select-all-item {
    padding: 0 !important;
}

.select-all-label {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0 20px; /* Khớp với padding mặc định của el-option */
    width: 100%;
    height: 34px;
    font-weight: normal;
    margin: 0;
    cursor: pointer;
}

.select-all-checkbox {
    cursor: pointer;
    margin: 0;
    flex-shrink: 0;
    accent-color: var(--blue-200);
}

.select-all-text {
    flex: 1;
    line-height: 34px;
    color: #606266;
    font-weight: 700;
}

/* Đảm bảo item có relative để checkmark định vị đúng */
::v-deep .el-select-dropdown__item {
    position: relative !important;
}
</style>

<style>
/* CSS GLOBAL: Áp dụng cho Dropdown được gắn vào Body
    Sử dụng class 'custom-tick-select' để không ảnh hưởng các select khác trong dự án
*/

/* 1. Tạo khoảng trống bên phải item để chứa dấu tick */
.el-select-dropdown.custom-tick-select .el-select-dropdown__item {
    padding-right: 35px !important; /* Đẩy chữ sang trái để không đè lên tick */
}

/* 2. Vẽ dấu tick cho item đang được chọn (Cả Single và Multiple) */
.el-select-dropdown.custom-tick-select .el-select-dropdown__item.selected::after {
    content: "\e6da";          /* Mã icon check trong font Element UI */
    font-family: 'element-icons';
    position: absolute;
    right: 20px;               /* Căn phải */
    top: 50%;
    transform: translateY(-50%);
    font-size: 12px;
    font-weight: bold;
    color: var(--blue-200);    /* Màu xanh của bạn */
    -webkit-font-smoothing: antialiased;
}

/* 3. Xử lý hover */
.el-select-dropdown.custom-tick-select .el-select-dropdown__item:hover,
.el-select-dropdown.custom-tick-select .el-select-dropdown__item.hover {
    background-color: #f0f8ff !important;
    color: var(--blue-200) !important;
}

/* 4. Fix màu chữ khi đã chọn */
.el-select-dropdown.custom-tick-select .el-select-dropdown__item.selected {
    color: var(--black-common) !important;
    background-color: #fff; /* Nền trắng khi chọn xong */
}

/* 5. Ghi đè style mặc định của Element UI Multiple để tránh bị duplicate tick hoặc lệch vị trí */
.el-select-dropdown.custom-tick-select.is-multiple .el-select-dropdown__item.selected::after {
    /* Element UI mặc định set right: 20px, ta giữ nguyên hoặc ghi đè nếu cần */
    right: 20px;
}
</style>